import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import { CDN_TYPE_CMS, imageUrlCDN } from 'utils';

import { Slider } from '@atlas/components';
import { Logger, NotificationManager, SiteConfigManager, useSettings } from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';
import {
  ChipsItem,
  isAvailableMission,
  Label,
  SmarticoMissionCard,
  SmarticoMissionSkeleton,
} from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import {
  getSmarticoMissions,
  requestMissionClaimReward,
  startMission,
  useSmartico,
} from 'components/ClientProfile/Achievements/Smartico';
import { renderMissionActionButton } from 'components/Smartico/smartico-helpers';
import { useIsRtl } from 'hooks/useIsRtl';

const LobbySmarticoMissionsBlock = ({
  blockTitleKey,
  imageUrl,
  maxMissionsCount,
  smarticoMissionsCategory,
}) => {
  const missionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SMARTICO_MISSIONS);
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = () => toggleVisibilityImg(false);
  const [missions, setMissions] = useState([]);
  const { lang } = useSettings();
  const { push: navigate } = useHistory();
  const [loading, setLoading] = useState(true);
  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  const { isReadySmartico } = useSmartico();

  const missionsUpdates = useCallback(
    loadedMissions => {
      if (!loadedMissions || !Array.isArray(loadedMissions) || loadedMissions.length === 0) {
        setMissions([]);
        setLoading(false);
        return;
      }

      Logger.debug('Smartico.missions', loadedMissions);
      const missionsFilter = smarticoMissionsCategory
        ? mission =>
            mission.ribbon?.toLowerCase() === smarticoMissionsCategory.toLowerCase() &&
            isAvailableMission(mission)
        : isAvailableMission;

      const availableMissions = (loadedMissions?.filter(missionsFilter) || []).sort((a, b) => {
        const aOrder = Number.isInteger(a.position) ? a.position : 9999;
        const bOrder = Number.isInteger(b.position) ? b.position : 9999;

        return aOrder - bOrder;
      });
      setMissions(
        maxMissionsCount ? availableMissions.splice(0, maxMissionsCount) : availableMissions,
      );
      setLoading(false);
    },
    [maxMissionsCount, smarticoMissionsCategory],
  );

  const loadMissions = useCallback(async () => {
    try {
      await getSmarticoMissions({
        cb: missionsUpdates,
        missionsUpdates,
      });
    } catch (e) {
      console.error(e);
      setMissions([]);
    } finally {
      setLoading(false);
    }
  }, [missionsUpdates]);

  const onClaimMissionReward = useCallback(
    async mission => {
      setLoading(true);

      try {
        await requestMissionClaimReward({
          achCompletedId: mission.ach_completed_id,
          missionId: mission.id,
        });
      } catch (e) {
        console.error(e);
      } finally {
        await loadMissions();
        setLoading(false);
      }
    },
    [loadMissions],
  );

  const onStartMission = useCallback(
    async missionId => {
      setLoading(true);

      try {
        await startMission({
          missionId,
        });
      } catch (e) {
        console.error(e);
      } finally {
        await loadMissions();
        setLoading(false);
      }
    },
    [loadMissions],
  );

  const onOpenMissionDetails = useCallback(
    mission => {
      NotificationManager.display({
        data: {
          mission,
          onClaimMissionReward,
          onStartMission,
        },
        type: 'smarticoMissionDetails',
      });
    },
    [onClaimMissionReward, onStartMission],
  );

  useEffect(() => {
    let timeoutId;

    if (isReadySmartico) {
      setMissions([]);
      setLoading(true);
      timeoutId = setTimeout(() => {
        loadMissions();
      }, 1000);
    } else {
      timeoutId = setTimeout(() => {
        setLoading(false);
      }, 5000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isReadySmartico, loadMissions, lang]);

  if (!loading && (!missions || missions.length === 0 || maxMissionsCount === 0)) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-smartico-missions">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={24}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/missions.png`
                }
                width={24}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          <ChipsItem
            className="casino__category__view-all lobby-smartico-missions__view-all"
            label={
              <div className="casino__category__title__view">
                <Label className="casino__category__title__text" message="View_All" />
              </div>
            }
            onClick={() => navigate(missionsPath)}
          />
        </div>
      </div>

      <div className="casino-lobby__content">
        <div className="lobby-tournaments__slider">
          <Slider
            autoplay={false}
            dir={dir}
            direction="horizontal"
            loop={false}
            spaceBetween={8}
            speed={1000}
          >
            {!loading
              ? (missions || []).map(mission => (
                  <Slider.Slide key={mission.id}>
                    <SmarticoMissionCard
                      mission={mission}
                      onClick={() => onOpenMissionDetails(mission)}
                      renderActionButton={mission =>
                        renderMissionActionButton(mission, {
                          onClaimMissionReward,
                          onOpenMissionDetails,
                          onStartMission,
                        })
                      }
                    />
                  </Slider.Slide>
                ))
              : Array.from({ length: 5 }).map((_, index) => (
                  <Slider.Slide key={`skeleton-${index}`}>
                    <SmarticoMissionSkeleton />
                  </Slider.Slide>
                ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default LobbySmarticoMissionsBlock;
